import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { submitRescheduleRequest, validateRescheduleData } from '../lib/rescheduleService';

const RescheduleAppointment: React.FC = () => {
  const { language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const content = {
    en: {
      title: "Reschedule Appointment",
      subtitle: "Enter your email address to reschedule your appointment",
      form: {
        email: "Email Address",
        emailPlaceholder: "Enter the email used for booking",
        submit: "Reschedule Appointment"
      },
      success: {
        title: "Reschedule Request Sent",
        message: "Your reschedule request has been submitted. You will receive a confirmation email shortly.",
        backHome: "Back to Home"
      },
      error: {
        required: "Email address is required",
        invalidEmail: "Please enter a valid email address",
        generic: "Something went wrong. Please try again."
      }
    },
    da: {
      title: "Ombook Tid",
      subtitle: "Indtast din e-mailadresse for at ombooke din tid",
      form: {
        email: "E-mailadresse",
        emailPlaceholder: "Indtast den e-mail, du brugte til booking",
        submit: "Ombook Tid"
      },
      success: {
        title: "Ombookningsanmodning Sendt",
        message: "Din ombookningsanmodning er blevet indsendt. Du vil modtage en bekræftelsesmail snarest.",
        backHome: "Tilbage til Forsiden"
      },
      error: {
        required: "E-mailadresse er påkrævet",
        invalidEmail: "Indtast venligst en gyldig e-mailadresse",
        generic: "Noget gik galt. Prøv venligst igen."
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const trimmedEmail = email.trim();
    const validation = validateRescheduleData({ email: trimmedEmail });

    if (!validation.isValid) {
      setError(validation.errors[0]);
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('Submitting reschedule request for email:', trimmedEmail);
      
      const result = await submitRescheduleRequest({ email: trimmedEmail });
      
      if (result.success) {
        setIsSubmitted(true);
        setEmail('');
      } else {
        setError(result.error || content[language as 'en' | 'da'].error.generic);
      }
      
    } catch (err) {
      console.error('Error submitting reschedule:', err);
      setError(content[language as 'en' | 'da'].error.generic);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#A68B7C]/20 to-transparent opacity-50" />
              
              <div className="relative z-10">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h1
                  className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {content[language as 'en' | 'da'].success.title}
                </h1>
                
                <p className="text-lg text-gray-300 leading-relaxed mb-8">
                  {content[language as 'en' | 'da'].success.message}
                </p>
                
                <a
                  href="/"
                  className="inline-block px-6 py-3 bg-[#A68B7C] text-white font-medium rounded-lg hover:bg-[#95796A] transition-all duration-300 transform hover:scale-[1.02]"
                >
                  {content[language as 'en' | 'da'].success.backHome}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] pt-32 pb-20">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1
              className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as 'en' | 'da'].title}
            </h1>
            <p className="text-lg text-gray-400">
              {content[language as 'en' | 'da'].subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg animate-in fade-in-0 duration-300">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              </div>
            )}

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#A68B7C]/10 to-transparent opacity-50" />
              
              <div className="relative z-10">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                    {content[language as 'en' | 'da'].form.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleInputChange}
                    placeholder={content[language as 'en' | 'da'].form.emailPlaceholder}
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2A2A] border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C] transition-all duration-300"
                    required
                    autoComplete="email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !email.trim()}
                  className="w-full mt-6 px-6 py-3 bg-[#A68B7C] text-white font-medium rounded-lg hover:bg-[#95796A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </>
                  ) : content[language as 'en' | 'da'].form.submit}
                </button>
              </div>
            </div>

            <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-lg p-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-[#A68B7C] mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-gray-400">
                  <p>
                    {language === 'en' 
                      ? "After submitting your email, your reschedule request will be processed and you'll receive a confirmation email."
                      : "Efter at have indsendt din e-mail vil din ombookningsanmodning blive behandlet, og du vil modtage en bekræftelsesmail."
                    }
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RescheduleAppointment;